'use client';
import { useState, useCallback } from 'react';

export interface UseDisclosureProps {
  /**
   * The initial state of the disclosure
   */
  defaultOpen?: boolean;
  /**
   * Callback fired when the disclosure opens
   */
  onOpen?: () => void;
  /**
   * Callback fired when the disclosure closes
   */
  onClose?: () => void;
}

export interface UseDisclosureReturn {
  /**
   * Whether the disclosure is open
   */
  open: boolean;
  /**
   * Whether the disclosure is open (legacy)
   */
  isOpen: boolean;
  /**
   * Function to open the disclosure
   */
  onOpen: () => void;
  /**
   * Function to close the disclosure
   */
  onClose: () => void;
  /**
   * Function to toggle the disclosure
   */
  onToggle: () => void;
  /**
   * Function to set the disclosure state
   */
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Function to get the disclosure props for the trigger element
   */
  getDisclosureProps: (props?: Record<string, unknown>) => {
    'aria-expanded': boolean;
    'aria-controls'?: string;
    onClick: () => void;
  };
  /**
   * Function to get the disclosure props for the content element
   */
  getButtonProps: (props?: Record<string, unknown>) => {
    'aria-expanded': boolean;
    onClick: () => void;
  };
}

export function useDisclosure(props: UseDisclosureProps = {}): UseDisclosureReturn {
  const { defaultOpen = false, onOpen: onOpenProp, onClose: onCloseProp } = props;

  const [isOpen, setIsOpen] = useState(defaultOpen);

  const onOpen = useCallback(() => {
    setIsOpen(true);
    onOpenProp?.();
  }, [onOpenProp]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    onCloseProp?.();
  }, [onCloseProp]);

  const onToggle = useCallback(() => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  }, [isOpen, onOpen, onClose]);

  const getDisclosureProps = useCallback(
    (props: Record<string, unknown> = {}) => ({
      ...props,
      'aria-expanded': isOpen,
      onClick: onToggle,
    }),
    [isOpen, onToggle]
  );

  const getButtonProps = useCallback(
    (props: Record<string, unknown> = {}) => ({
      ...props,
      'aria-expanded': isOpen,
      onClick: onToggle,
    }),
    [isOpen, onToggle]
  );

  return {
    open: isOpen,
    isOpen,
    onOpen,
    onClose,
    onToggle,
    setOpen: setIsOpen,
    getDisclosureProps,
    getButtonProps,
  };
}
